import { supabase } from '@/integrations/supabase/client';
import { captureException, addBreadcrumb, Sentry } from '@/lib/sentry';

export interface ErrorContext {
  userId?: string;
  sessionId?: string;
  url?: string;
  userAgent?: string;
  component?: string;
  action?: string;
  metadata?: Record<string, any>;
}

export type ErrorSeverity = 'low' | 'medium' | 'high' | 'critical';
export type ErrorType = 'javascript' | 'api' | 'database' | 'stripe' | 'auth' | 'cron' | 'webhook';

class ErrorReporter {
  private sessionId: string;
  private isEnabled: boolean = true;

  constructor() {
    this.sessionId = this.generateSessionId();
    
    // Set up global error handlers
    if (typeof window !== 'undefined') {
      this.setupGlobalHandlers();
    }
  }

  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private setupGlobalHandlers() {
    // Catch unhandled JavaScript errors
    window.addEventListener('error', (event) => {
      this.logError({
        type: 'javascript',
        severity: 'high',
        message: event.message,
        stackTrace: event.error?.stack,
        context: {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
          url: window.location.href
        }
      });
    });

    // Catch unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.logError({
        type: 'javascript',
        severity: 'high',
        message: `Unhandled Promise Rejection: ${event.reason}`,
        stackTrace: event.reason?.stack,
        context: {
          url: window.location.href,
          reason: String(event.reason)
        }
      });
    });
  }

  async logError({
    type,
    severity,
    message,
    stackTrace,
    context = {}
  }: {
    type: ErrorType;
    severity: ErrorSeverity;
    message: string;
    stackTrace?: string;
    context?: ErrorContext;
  }): Promise<void> {
    if (!this.isEnabled) return;

    try {
      // Get current user if available
      const { data: { user } } = await supabase.auth.getUser();
      
      // Prepare error data
      const errorData = {
        error_type: type,
        severity,
        message: message.substring(0, 2000), // Limit message length
        stack_trace: stackTrace?.substring(0, 8000), // Limit stack trace
        user_id: user?.id || context.userId,
        session_id: this.sessionId,
        user_agent: navigator.userAgent,
        url: context.url || window.location.href,
        context: {
          ...context,
          timestamp: new Date().toISOString(),
          viewport: {
            width: window.innerWidth,
            height: window.innerHeight
          }
        }
      };

      // Log to Supabase
      const { error } = await supabase
        .from('application_errors')
        .insert(errorData);

      if (error) {
        console.error('Failed to log error to database:', error);
        // Fallback to console logging
        console.error('Original error:', { type, severity, message, stackTrace, context });
      }

      // Also send to Sentry if available
      try {
        if (stackTrace) {
          const errorObj = new Error(message);
          errorObj.stack = stackTrace;
          captureException(errorObj, {
            extra: context,
            tags: {
              errorType: type,
              severity,
              sessionId: this.sessionId
            }
          });
        } else {
          Sentry.captureMessage(message, {
            level: this.mapSeverityToSentryLevel(severity),
            extra: context,
            tags: {
              errorType: type,
              severity,
              sessionId: this.sessionId
            }
          });
        }
      } catch (sentryError) {
        // Don't break if Sentry fails
        console.warn('Sentry logging failed:', sentryError);
      }

    } catch (logError) {
      // Don't let error logging break the app
      console.error('Error reporting failed:', logError);
      console.error('Original error:', { type, severity, message, stackTrace, context });
    }
  }

  // Map severity to Sentry levels
  private mapSeverityToSentryLevel(severity: ErrorSeverity): 'fatal' | 'error' | 'warning' | 'info' {
    switch (severity) {
      case 'critical': return 'fatal';
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'info';
      default: return 'error';
    }
  }

  // Log API errors
  logApiError(error: any, endpoint: string, method: string = 'GET') {
    // Add breadcrumb for API context
    addBreadcrumb(`API Call: ${method} ${endpoint}`, 'http', {
      endpoint,
      method,
      status: error.status
    });

    this.logError({
      type: 'api',
      severity: 'medium',
      message: `API Error: ${method} ${endpoint} - ${error.message || error}`,
      stackTrace: error.stack,
      context: {
        endpoint,
        method,
        status: error.status,
        response: error.response?.data
      }
    });
  }

  // Log Stripe errors
  logStripeError(error: any, action: string) {
    this.logError({
      type: 'stripe',
      severity: 'high',
      message: `Stripe Error: ${action} - ${error.message || error}`,
      context: {
        action,
        stripeCode: error.code,
        stripeType: error.type,
        stripeParam: error.param
      }
    });
  }

  // Log authentication errors
  logAuthError(error: any, action: string) {
    this.logError({
      type: 'auth',
      severity: 'medium',
      message: `Auth Error: ${action} - ${error.message || error}`,
      context: {
        action,
        authCode: error.code
      }
    });
  }

  // Log database errors
  logDatabaseError(error: any, query: string) {
    this.logError({
      type: 'database',
      severity: 'high',
      message: `Database Error: ${error.message || error}`,
      stackTrace: error.stack,
      context: {
        query: query.substring(0, 500), // Limit query length
        code: error.code,
        details: error.details
      }
    });
  }

  // Disable error reporting (for development)
  disable() {
    this.isEnabled = false;
  }

  // Enable error reporting
  enable() {
    this.isEnabled = true;
  }

  // Track metrics
  async trackMetric(name: string, value: number, type: 'counter' | 'gauge' | 'histogram' = 'counter', tags: Record<string, any> = {}) {
    if (!this.isEnabled) return;

    try {
      await supabase
        .from('application_metrics')
        .insert({
          metric_name: name,
          metric_value: value,
          metric_type: type,
          tags
        });
    } catch (error) {
      console.error('Failed to track metric:', error);
    }
  }
}

// Export singleton instance
export const errorReporter = new ErrorReporter();

// Disable in development to avoid noise
if (process.env.NODE_ENV === 'development') {
  errorReporter.disable();
}
